/**
 * The app-wide SOR access token. doc-web fills it through <SorTokenSync> once the tenant settings
 * have SOR active; a host app such as PAL can do the same or call setSorToken / setSorTokenProvider.
 * Every useSorApi call reads the token from here, and a new one is fetched when it expires.
 */

/** Fetches a SOR access token; `forceRefresh` asks for a new one instead of a cached one. */
export type SorTokenProvider = (options: { forceRefresh: boolean }) => Promise<string>;

/** A tenant settings row from GET /api/v2/settings */
export type SorTenantSettingRow = {
  ability?: string;
  is_active?: boolean;
};

// A token this close to its expiry is treated as expired
const EXPIRY_MARGIN_MS = 60_000;
// Refreshes that land closer together than this would loop on a token that's already short-lived
const MIN_REFRESH_DELAY_MS = 5_000;

let token: string | null = null;
let expiresAt: number | null = null;
let provider: SorTokenProvider | null = null;
/** null until the tenant settings are known */
let enabled: boolean | null = null;
/** Set while <SorTokenSync> decides from the tenant settings whether a token is fetched */
let managed = false;
// Token requests made before the tenant settings arrive
let settingsWaiters: Array<() => void> = [];
let pending: Promise<string> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped on every reset so a fetch that started earlier can't store its token afterwards
let generation = 0;
// Notified whenever the stored token changes (see subscribeSorToken)
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

/** Calls `listener` whenever the stored token is set, renewed or dropped; returns the unsubscribe. */
export function subscribeSorToken(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The stored token as last set, without the expiry check, so it stays stable between change
 * notifications (for useSyncExternalStore). It is renewed before it expires while a provider is set.
 */
export function getSorTokenSnapshot(): string | null {
  return token;
}

/** Whether the tenant settings have the SOR ability active */
export function isSorActiveInSettings(settings: ReadonlyArray<SorTenantSettingRow> | null | undefined): boolean {
  return !!settings?.some((row) => String(row?.ability ?? '').toLowerCase() === 'sor' && row.is_active === true);
}

/** The `exp` claim of a JWT in ms, or null when the token isn't a JWT */
export function readJwtExpiry(jwt: string): number | null {
  const payload = jwt.split('.')[1];
  if (!payload) return null;
  try {
    // Base64url → base64 (the lib target here predates replaceAll)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const exp = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function clearRefreshTimer(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}

// Renews the token shortly before it expires, so callers rarely wait for one
function scheduleRefresh(): void {
  clearRefreshTimer();
  if (!provider || !expiresAt || enabled === false) return;
  const delay = Math.max(expiresAt - EXPIRY_MARGIN_MS - Date.now(), MIN_REFRESH_DELAY_MS);
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    // A failed renewal leaves the old token; the next call fetches again once it expires
    fetchToken(true).catch(() => undefined);
  }, delay);
}

function storeToken(value: string, expiresInSeconds?: number): void {
  token = value;
  expiresAt = expiresInSeconds != null ? Date.now() + expiresInSeconds * 1000 : readJwtExpiry(value);
  scheduleRefresh();
  notifyListeners();
}

function hasValidToken(): boolean {
  return !!token && (expiresAt == null || expiresAt - EXPIRY_MARGIN_MS > Date.now());
}

function fetchToken(forceRefresh: boolean, fallback?: SorTokenProvider): Promise<string> {
  const fetcher = provider ?? fallback;
  if (!fetcher) return Promise.reject(new Error('No SOR token provider is set'));
  // One request at a time, shared by every caller
  if (pending) return pending;
  const startedIn = generation;
  pending = fetcher({ forceRefresh })
    .then((value) => {
      if (startedIn === generation) storeToken(value);
      return value;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

/**
 * Sets the SOR token, e.g. one PAL already holds. Its expiry is read from the JWT unless
 * `expiresInSeconds` is given; null clears it.
 */
export function setSorToken(value: string | null, expiresInSeconds?: number): void {
  if (!value) {
    clearSorToken();
    return;
  }
  storeToken(value, expiresInSeconds);
}

/** Sets how a new token is fetched when there is none or it has expired; null removes it. */
export function setSorTokenProvider(next: SorTokenProvider | null): void {
  provider = next;
  scheduleRefresh();
}

function releaseSettingsWaiters(): void {
  const waiters = settingsWaiters;
  settingsWaiters = [];
  waiters.forEach((resolve) => resolve());
}

/** Records whether the tenant has SOR active; false drops the stored token. */
export function setSorEnabled(next: boolean | null): void {
  enabled = next;
  if (next === false) clearSorToken();
  else scheduleRefresh();
  if (next !== null) releaseSettingsWaiters();
}

/**
 * While true, token requests wait for the tenant settings (setSorEnabled) instead of fetching
 * a token straight away, and are refused when SOR is not active. Set by <SorTokenSync>.
 */
export function setSorTokenManaged(next: boolean): void {
  managed = next;
  // Nobody will report the settings any more, so let waiting requests fetch as before
  if (!next) releaseSettingsWaiters();
}

function waitForSettings(): Promise<void> {
  if (!managed || enabled !== null) return Promise.resolve();
  return new Promise((resolve) => settingsWaiters.push(resolve));
}

/** Thrown when the tenant settings have SOR turned off */
export class SorDisabledError extends Error {
  constructor() {
    super('The System of Record is not enabled for this tenant.');
    this.name = 'SorDisabledError';
  }
}

/** Whether SOR is active for the tenant: null until the tenant settings are known. */
export function isSorEnabled(): boolean | null {
  return enabled;
}

/** The stored token if it hasn't expired, without fetching one. */
export function getStoredSorToken(): string | null {
  return hasValidToken() ? token : null;
}

/** Drops the stored token, e.g. on logout or a tenant switch. */
export function clearSorToken(): void {
  generation += 1;
  const hadToken = token != null;
  token = null;
  expiresAt = null;
  pending = null;
  clearRefreshTimer();
  if (hadToken) notifyListeners();
}

/**
 * A valid SOR token: the stored one, or a new one from the provider when there is none or it has
 * expired. `fallback` fetches the token when no provider is set. While the token is managed
 * (setSorTokenManaged), this waits for the tenant settings and rejects when SOR is off.
 */
export async function getSorAccessToken(fallback?: SorTokenProvider): Promise<string> {
  await waitForSettings();
  if (managed && enabled === false) throw new SorDisabledError();
  if (hasValidToken()) return token as string;
  // An expired token is still in Auth0's cache until its own margin passes, so ask for a new one
  return fetchToken(token != null, fallback);
}

/** Drops a token the SOR API rejected and fetches a new one. */
export function renewSorToken(rejected: string, fallback?: SorTokenProvider): Promise<string> {
  if (token === rejected) {
    token = null;
    expiresAt = null;
    clearRefreshTimer();
    notifyListeners();
  }
  return token ? Promise.resolve(token) : fetchToken(true, fallback);
}
